import { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faCircleCheck, faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { TextField, Button } from '@mui/material';
import './feedback.css';

function Feedback() {
  const { t } = useTranslation();
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState('');

  const canSubmit = feedback.trim().length > 0 && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setStatus(null);
    setMessage('');
    try {
      const userId = localStorage.getItem('user_id');
      const resp = await fetch(`${process.env.REACT_APP_API_BASE_URL}feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId ? parseInt(userId, 10) : null,
          feedback: feedback.trim(),
          timestamp: new Date().toISOString()
        })
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || `HTTP ${resp.status}`);
      }
      setStatus('success');
      setMessage(t('feedback_success_message'));
      setFeedback('');
    } catch (err) {
      setStatus('error');
      setMessage(t('feedback_error_message'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="feedback-parent-container">
      <div className="feedback-info-box">
        <FontAwesomeIcon className="feedback-info-icon" icon={faCircleInfo} />
        <div className="feedback-info-text">
          <h3 className="feedback-title">{t('feedback_title')}</h3>
          <p>
            <Trans i18nKey="feedback_blurb1_html" components={{ strong: <strong /> }} />
          </p>
          <p style={{ marginTop: '8px' }}>
            <Trans i18nKey="feedback_blurb2_html" components={{ strong: <strong /> }} />
          </p>
        </div>
      </div>

      <form className="feedback-form-card" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label={t('feedback_field_label')}
          variant="outlined"
          placeholder={t('feedback_field_placeholder')}
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          multiline
          rows={5}
          margin="none"
          sx={{
            '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: '#5616ea' } },
            '& .MuiOutlinedInput-root.Mui-error fieldset': { borderColor: '#dc0e0e' },
            '& .MuiInputLabel-root': { color: 'gray', '&.Mui-focused': { color: '#5616ea' }, '&.Mui-error': { color: '#dc0e0e' } },
          }}
        />

        <Button
          type="submit"
          disabled={!canSubmit}
          variant="contained"
          sx={{
            backgroundColor: '#5616ea',
            '&:hover': { backgroundColor: '#4a14d6' },
            borderRadius: '14px',
            fontSize: '15px',
            fontWeight: 600,
            textTransform: 'none',
            padding: '10px',
            width: '180px'
          }}
        >
          {submitting ? <span className="feedback-loading-spinner" /> : t('submit')}
        </Button>

        {status === 'success' && (
          <div className="feedback-status success">
            <FontAwesomeIcon icon={faCircleCheck} className="feedback-status-icon success" />
            <span>{message}</span>
          </div>
        )}
        {status === 'error' && (
          <div className="feedback-status error">
            <FontAwesomeIcon icon={faCircleXmark} className="feedback-status-icon error" />
            <span>{message}</span>
          </div>
        )}
      </form>
    </div>
  );
}

export default Feedback;
